import { useCallback } from 'react';

import { datadogRum } from '@datadog/browser-rum';

import { useBrazeContext } from 'state/braze';
import { useLDContext } from 'state/launchdarkly';
import { useMParticleContext } from 'state/mParticle';
import { getCustomerIdForCRMStack } from 'utils/environment';
import LogRocketHelper from 'utils/logrocket';

import { UserDetails } from './types';

export const useThirdPartyAuthentication = () => {
  const { setUserId: brazeSetUserId } = useBrazeContext();
  const { login: launchDarklyLogin, logout: launchDarklyLogout } = useLDContext();
  const { login: mParticleLogin, logout: mParticleLogout } = useMParticleContext();

  const logUserInToThirdPartyServices = useCallback(
    (currentUser: UserDetails) => {
      launchDarklyLogin(currentUser);

      const sanitizedDetails = Object.keys(currentUser.details).reduce(
        (acc: { [key: string]: string | null }, key: string) => {
          return { ...acc, [key]: currentUser.details[key]?.toString() || null };
        },
        {}
      );

      const customerid = getCustomerIdForCRMStack(
        currentUser.cognitoId,
        currentUser.thLegacyCognitoId
      );

      mParticleLogin({
        customerid,
        rbiCognitoId: currentUser.cognitoId,
        email_subscribe: currentUser.details.promotionalEmails ? 'opted_in' : 'unsubscribed',
        ...sanitizedDetails,
      });

      if (customerid) {
        brazeSetUserId(customerid);
      }

      const lrIdentify = () => {
        const logRocketInstance = LogRocketHelper.getInstance();
        logRocketInstance.logrocket?.identify(currentUser.cognitoId);
      };

      // Run logrocket.identify on init in case we are already logged in
      LogRocketHelper.getInstance().runOnInit = lrIdentify;

      // Run logrocket.identify in case a LR session was initialized before we were logged in
      lrIdentify();
    },
    [brazeSetUserId, launchDarklyLogin, mParticleLogin]
  );

  const logUserOutOfThirdPartyServices = useCallback(() => {
    launchDarklyLogout();
    mParticleLogout();
    brazeSetUserId(null);
    datadogRum.clearUser();

    const logRocketInstance = LogRocketHelper.getInstance();
    if (logRocketInstance.logrocket) {
      logRocketInstance.logrocket.identify('');
    }
  }, [brazeSetUserId, launchDarklyLogout, mParticleLogout]);

  return {
    logUserInToThirdPartyServices,
    logUserOutOfThirdPartyServices,
  };
};
